export interface PageResponse<T> {
  items: T[];
  hasNext: boolean;
}

export interface PageFilter {
  page: number;
  pageSize: number;
}

export type Environment = 'development' | 'staging' | 'production';

export enum WorkspacePurpose {
  NONE = 'NONE',
  TRIAL = 'TRIAL',
  PRODUCTION = 'PRODUCTION',
  HOMOLOG = 'HOMOLOG',
  TRAINING = 'TRAINING',
  INTERNAL = 'INTERNAL',
}

export enum WorkspaceStatus {
  INACTIVE = 'INACTIVE',
  PENDING = 'PENDING',
  ACTIVE = 'ACTIVE',
  REJECTED = 'REJECTED',
}

export interface AccountView {
  id: string;
  totvsCode: string;
  name: string;
  country: string;
  document: string;
}

export interface WorkspaceItem {
  id: string;
  totvsCode: string;
  name: string;
  subdomain: string;
  document: { value: string; country: string };
  racId: string;
  status: WorkspaceStatus;
}

export interface DocumentNumber {
  value: string;
  country: string;
}

export type Language = 'pt_BR' | 'en_US' | 'es_ES';

export interface System {
  id: string;
  ref: string;
  name: string;
}

export interface AddonComponent {
  name: string;
  ref: string;
  databaseName?: string;
  type?: ComponentType[]
  internalUrl: string;
  externalUrl: string;
}

export type ComponentType = 'INSIGHTS' | 'DATA_SHARING';

export interface Addon {
  id: string;
  ref: string;
  name: string;
  insightsProductFolderName?: string;
  components: AddonComponent[];
}

export interface ReprocessAddon {
  catalogData: CatalogData;
  workspaces?: string[];
}

export interface CatalogData {
  system: System;
  addon?: Addon;
}

export interface NewWorkspaceCommand {
  purpose: WorkspacePurpose;
  totvsCode: string;
  name: string;
  email: string;
  document?: DocumentNumber;
  language: string;
  catalogData: CatalogData;
}

export interface WorkspaceDetailResponse {
  id: string;
  name: string;
  subdomain: string;
  racAdminEmail: string;
  status: WorkspaceStatus;
  purpose: WorkspacePurpose;
  requesterEmail: string;
  racId: string;
  fluigId: string;
  fluigCompanyDomain: string;
  idp: string;
  period?: string;
  account: AccountView;
}

export interface WorkspaceSystemItem {
  systemId: string;
  active: boolean;
  addons: string[];
}

export interface SimpleWorkspace {
  workspaceId: string;
  racId: string;
  name: string;
}

export interface WorkspaceFilters {
  systemId: string;
  addonId: string;
  partialRacId?: string;
  partialName?: string;
}

export interface SystemWorkspace {
  workspaceId: string;
  racId: string;
  name: string;
  subdomain: string;
}

export interface SystemWorkspacesFilter {
  partialRacId?: string;
  partialName?: string;
  partialSubdomain?: string;
  page?: number;
  pageSize?: number;
}

export interface UpdateWorkspaceCommand {
  purpose: WorkspacePurpose;
  passwordDefinitionEnabled: boolean;
  requesterEmail: string;
  subdomain: string;
}

export interface Provision {
  id: string;
  origin: string;
  status: string;
  createdAt: string;
  createdBy: string;
  systemIds: string;
}

export interface ProvisionTask {
  id: string;
  provisionTaskType: string;
  provisionTaskStatus: string;
  error?: string;
  systemIds: string;
}

export interface ProvisionSubTask {
  subTaskId: string;
  taskId: string;
  component: string;
  status: string;
  error?: string;
}

export interface SystemAddonsResponse {
  items: SystemAddonItem[];
}

export interface SystemAddonItem {
  id: string;
  name: string;
  enabled: boolean;
}

export interface LinkAddonDialogProps {
  open: boolean;
  onClose: () => void;
  onLink: (selectedAddons: string[], selectedResources?: string[]) => void;
  currentAddons: string[];
  systemOwner: string;
}

export interface MigrationPeriod {
  start: Date;
  end: Date;
}

/**
 * Command to retry a system provision.
 */
export interface RetrySystemProvisionCommand {
  catalogData: CatalogData;
}

